import {
  AbstractControl,
  FormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

/**
 * Validadores e normalizações dos campos da NFS-e padrão nacional (Reforma
 * Tributária): dados fiscais da empresa, `TaxData` do plano e serviço avulso.
 *
 * O backend valida só o **formato** (a combinação cTribNac/NBS/CST/cClassTrib/
 * cIndOp só é conferida pela prefeitura). As regras aqui espelham as dele para
 * que um valor recusado nunca chegue a ser enviado.
 *
 * Todos os campos são opcionais: vazio (`null`, `undefined`, `''`) é sempre
 * válido e vai ao backend como `null` — uma string vazia seria lida como valor
 * informado e recusada ("deve ter 6 dígitos").
 */

/** Chave de erro dos validadores daqui; o valor é a chave i18n da mensagem. */
export const NFSE_ERROR_KEY = 'nfse';

/** Chave de erro para a mensagem já pronta devolvida pelo backend. */
export const SERVER_ERROR_KEY = 'serverError';

/** Regime de apuração do Simples Nacional (`Tax.SimplesNacionalRegime`). */
export enum ESimplesNacionalRegime {
  /** Tributos federais e ISS apurados pelo Simples (DAS). */
  FederalAndIss = 1,
  /** Tributos federais pelo Simples, ISS fora do Simples. */
  FederalOnly = 2,
  /** Sublimite excedido: tributos federais e ISS fora do Simples. */
  SublimitExceeded = 3,
}

export const SIMPLES_NACIONAL_REGIMES: readonly ESimplesNacionalRegime[] = [
  ESimplesNacionalRegime.FederalAndIss,
  ESimplesNacionalRegime.FederalOnly,
  ESimplesNacionalRegime.SublimitExceeded,
];

/** Dígitos e `. , / -`, até 20 caracteres — cada prefeitura tem o seu formato. */
const MUNICIPAL_TRIBUTARY_CODE_PATTERN = /^[\d.,/-]{1,20}$/;

/** Pontuação aceita na digitação do NBS (`1.1502.10.00`) e removida no envio. */
const NBS_PUNCTUATION = /[.\s-]/g;

const NBS_CODE_PATTERN = /^\d{9}$/;

function isBlank(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  );
}

function nfseError(message: string): ValidationErrors {
  return { [NFSE_ERROR_KEY]: message };
}

/** `''`/espaços viram `null`; o resto segue sem espaços nas pontas. */
export function normalizeNfseCode(value: unknown): string | null {
  return isBlank(value) ? null : String(value).trim();
}

/** NBS sem pontuação (`1.1502.10.00` → `115021000`); vazio vira `null`. */
export function normalizeNbsCode(value: unknown): string | null {
  return isBlank(value) ? null : String(value).replace(NBS_PUNCTUATION, '');
}

/** Número a partir do valor do controlo (texto do input de moeda ou número); vazio vira `null`. */
export function normalizeNfseNumber(value: unknown): number | null {
  if (isBlank(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Exatamente `length` dígitos (cTribNac, cIndOp, CST, cClassTrib). */
export function nfseDigitsValidator(
  length: number,
  message: string
): ValidatorFn {
  const pattern = new RegExp(`^\\d{${length}}$`);
  return (control: AbstractControl): ValidationErrors | null =>
    isBlank(control.value) || pattern.test(String(control.value).trim())
      ? null
      : nfseError(message);
}

/** Código de tributação municipal: sem máscara fixa, só o formato amplo. */
export function nfseMunicipalTributaryCodeValidator(
  message: string
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null =>
    isBlank(control.value) ||
    MUNICIPAL_TRIBUTARY_CODE_PATTERN.test(String(control.value).trim())
      ? null
      : nfseError(message);
}

/** NBS com 9 dígitos depois de retirada a pontuação de digitação. */
export function nfseNbsCodeValidator(message: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const normalized = normalizeNbsCode(control.value);
    return normalized === null || NBS_CODE_PATTERN.test(normalized)
      ? null
      : nfseError(message);
  };
}

/** Regime do Simples Nacional: `1`, `2`, `3` ou vazio. */
export function simplesNacionalRegimeValidator(message: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isBlank(control.value)) return null;
    return SIMPLES_NACIONAL_REGIMES.includes(Number(control.value))
      ? null
      : nfseError(message);
  };
}

/** Percentual de 0 a 100 com até 2 casas decimais. */
export function nfsePercentValidator(messages: {
  range: string;
  decimals: string;
}): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isBlank(control.value)) return null;
    const value = Number(control.value);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      return nfseError(messages.range);
    }
    // Compara em centésimos, com folga para o erro de ponto flutuante (6.5 * 100).
    const cents = value * 100;
    return Math.abs(cents - Math.round(cents)) < 1e-6
      ? null
      : nfseError(messages.decimals);
  };
}

/**
 * Só aceita valor quando o controlo irmão `siblingName` estiver preenchido
 * (ex.: a alíquota do Simples exige o regime). Precisa de um `FormGroup` pai,
 * e quem altera o irmão tem de revalidar este controlo.
 */
export function nfseRequiresSiblingValidator(
  siblingName: string,
  message: string
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isBlank(control.value)) return null;
    const sibling = control.parent?.get(siblingName);
    if (!sibling) return null;
    return isBlank(sibling.value) ? nfseError(message) : null;
  };
}

/**
 * Campos da NFS-e padrão nacional de um item faturável — o `TaxData` do plano
 * e a raiz do serviço avulso têm os mesmos cinco, com as mesmas regras.
 */
export const NFSE_ITEM_FIELDS = [
  'NationalTributaryCode',
  'MunicipalTributaryCode',
  'IbsCbsOperationCode',
  'IbsCbsCst',
  'IbsCbsClassificationCode',
] as const;

export type TNfseItemField = (typeof NFSE_ITEM_FIELDS)[number];

/** Controlos dos cinco campos de NFS-e de um item, já com os validadores. */
export function createNfseItemControls(): Record<TNfseItemField, FormControl> {
  return {
    NationalTributaryCode: new FormControl(null, [
      nfseDigitsValidator(6, 'NFSE_NATIONAL.ERRORS.NATIONAL_TRIBUTARY_CODE'),
    ]),
    MunicipalTributaryCode: new FormControl(null, [
      nfseMunicipalTributaryCodeValidator(
        'NFSE_NATIONAL.ERRORS.MUNICIPAL_TRIBUTARY_CODE'
      ),
    ]),
    IbsCbsOperationCode: new FormControl(null, [
      nfseDigitsValidator(6, 'NFSE_NATIONAL.ERRORS.IBS_CBS_OPERATION_CODE'),
    ]),
    IbsCbsCst: new FormControl(null, [
      nfseDigitsValidator(3, 'NFSE_NATIONAL.ERRORS.IBS_CBS_CST'),
    ]),
    IbsCbsClassificationCode: new FormControl(null, [
      nfseDigitsValidator(6, 'NFSE_NATIONAL.ERRORS.IBS_CBS_CLASSIFICATION_CODE'),
    ]),
  };
}

/** NBS do item: 9 dígitos; a pontuação digitada é aceita e removida no envio. */
export function nfseItemNbsCodeValidator(): ValidatorFn {
  return nfseNbsCodeValidator('NFSE_NATIONAL.ERRORS.NBS_CODE');
}

/**
 * Os cinco campos de NFS-e e o NBS como o backend os espera: vazio vira
 * `null` e o NBS vai sem pontuação. `PUT /Plan` e `PUT /Service` gravam o
 * registro inteiro — campo ausente é apagado —, por isso vão sempre.
 */
export function normalizeNfseItemFields(
  source: Partial<Record<TNfseItemField | 'NbsCode', unknown>> | null | undefined
): Record<TNfseItemField | 'NbsCode', string | null> {
  return {
    NbsCode: normalizeNbsCode(source?.NbsCode),
    NationalTributaryCode: normalizeNfseCode(source?.NationalTributaryCode),
    MunicipalTributaryCode: normalizeNfseCode(source?.MunicipalTributaryCode),
    IbsCbsOperationCode: normalizeNfseCode(source?.IbsCbsOperationCode),
    IbsCbsCst: normalizeNfseCode(source?.IbsCbsCst),
    IbsCbsClassificationCode: normalizeNfseCode(
      source?.IbsCbsClassificationCode
    ),
  };
}

export type TServerValidationError = {
  PropertyName?: string | null;
  ErrorMessage?: string | null;
};

/**
 * Pinta nos controlos as mensagens de `ValidationResult.Errors` devolvidas pelo
 * backend. `resolve` diz a que controlo cada erro pertence (pelo
 * `PropertyName` ou, quando o backend devolve o nome do bloco, pela
 * mensagem). O erro sai sozinho na próxima edição do campo, porque a
 * revalidação recalcula `errors` a partir dos validadores.
 *
 * @returns Os controlos que receberam erro, na ordem em que vieram.
 */
export function applyServerValidationErrors(
  errors: TServerValidationError[] | null | undefined,
  resolve: (error: TServerValidationError) => AbstractControl | null | undefined
): AbstractControl[] {
  const applied: AbstractControl[] = [];
  for (const error of errors ?? []) {
    const message = error?.ErrorMessage;
    if (!message) continue;
    const control = resolve(error);
    if (!control) continue;
    control.setErrors({ ...(control.errors ?? {}), [SERVER_ERROR_KEY]: message });
    control.markAsTouched();
    applied.push(control);
  }
  return applied;
}

/** `ValidationResult.Errors` de uma resposta de erro HTTP (`error.error`). */
export function getServerValidationErrors(
  httpError: unknown
): TServerValidationError[] {
  const body = (httpError as { error?: unknown } | null)?.error as
    | { ValidationResult?: { Errors?: unknown } }
    | null
    | undefined;
  const errors = body?.ValidationResult?.Errors;
  return Array.isArray(errors)
    ? errors.filter(
        (error): error is TServerValidationError =>
          !!error && typeof error === 'object'
      )
    : [];
}
